import { useCallback, useMemo, useRef, useState } from "react";
import { toastManager, type SelectAllGroup } from "@salestrak/ui";
import type { IssueRow, StageOption } from "#app/lib/data";
import { StageSelector, AssigneeSelector } from "#app/components/entity-selectors";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  BulkOperation,
  BulkMode,
  type AnalyticsScope,
  type AnalyticsView,
} from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

/**
 * The shared bulk-action popover bodies for issue selections — extracted from
 * `issues-table.tsx` so anything listing issue rows (the list view, a widget's
 * drilldown dialog) mounts the SAME stage/assignee editors instead of forking
 * them. Callers pass the analytics `view`/`scope` so each reports where the
 * bulk operation happened; the mutation callbacks arrive as
 * {@link BulkIssueMutations} (from `useIssueListMutations().bulk`).
 */

/** Dedicated bulk mutation callbacks — single call per operation. */
export type BulkIssueMutations = {
  changeStage: (issueIds: string[], stageId: string) => Promise<void>;
  changeStageByWhere: (
    where: Record<string, unknown>,
    stageId: string,
  ) => Promise<void>;
  addAssignees: (issueIds: string[], entityIds: string[]) => Promise<void>;
  addAssigneesByWhere: (
    where: Record<string, unknown>,
    entityIds: string[],
  ) => Promise<void>;
  removeAssignees: (issueIds: string[], entityIds: string[]) => Promise<void>;
  removeAssigneesByWhere: (
    where: Record<string, unknown>,
    entityIds: string[],
  ) => Promise<void>;
  setPrimaryAssignee: (issueIds: string[], entityId: string) => Promise<void>;
  setPrimaryAssigneeByWhere: (
    where: Record<string, unknown>,
    entityId: string,
  ) => Promise<void>;
};

export type BulkCommit<TData> = (
  fn: (rows: TData[]) => Promise<void> | void,
  successMessage?: string,
) => void;

/**
 * When select-all is active for one or more groups, merge their where clauses
 * into a single where object. Returns undefined when no groups are select-all.
 */
export function buildSelectAllWhere(
  selectAllGroups: SelectAllGroup[],
  getGroupWhere: (groupKey: string) => Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (selectAllGroups.length === 0) return undefined;
  if (selectAllGroups.length === 1)
    return getGroupWhere(at(selectAllGroups, 0, "select-all group").groupKey);
  return { OR: selectAllGroups.map((g) => getGroupWhere(g.groupKey)) };
}

function pluralProjects(n: number) {
  return `${n} project${n !== 1 ? "s" : ""}`;
}

export function BulkStageContent({
  view,
  scope,
  workflowId,
  commit,
  bulkMutations,
  refetchGroups,
  availableStages,
  selectedCount,
  selectAllWhere,
}: Omittable<{
  /** Which view the bulk operation happened on, for analytics. */
  view: AnalyticsView;
  /** The scope kind that view was showing, for analytics. */
  scope: AnalyticsScope;
  workflowId: string;
  commit: BulkCommit<IssueRow>;
  bulkMutations: BulkIssueMutations;
  refetchGroups: () => Promise<unknown>;
  availableStages: StageOption[];
  selectedCount: number;
  selectAllWhere?: Record<string, unknown>;
}>) {
  const { track } = useAnalytics();
  return (
    <StageSelector
      variant="inline"
      workflowId={workflowId}
      onValueChange={(stageId) => {
        const stageName =
          availableStages.find((s) => s.stageId === stageId)?.name ?? "stage";
        commit(
          async (rows) => {
            if (selectAllWhere) {
              await bulkMutations.changeStageByWhere(selectAllWhere, stageId);
            } else {
              await bulkMutations.changeStage(
                rows.map((r) => r.issueId),
                stageId,
              );
            }
            await refetchGroups();
            track({
              context: AnalyticsContext.Issue,
              event: IssueEvent.BulkChangeStage,
              data: {
                view,
                scope,
                mode: selectAllWhere ? BulkMode.Where : BulkMode.Ids,
                count: selectedCount,
              },
            });
          },
          `${pluralProjects(selectedCount)} moved to ${stageName}`,
        );
      }}
    />
  );
}

export function BulkAssigneeContent({
  view,
  scope,
  bulkMutations,
  selectedRows,
  selectAllWhere,
}: Omittable<{
  /** Which view the bulk operation happened on, for analytics. */
  view: AnalyticsView;
  /** The scope kind that view was showing, for analytics. */
  scope: AnalyticsScope;
  bulkMutations: BulkIssueMutations;
  selectedRows: IssueRow[];
  selectAllWhere?: Record<string, unknown>;
}>) {
  const { track } = useAnalytics();
  // Union of all assignee IDs across selected issues
  const initialIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of selectedRows) {
      for (const a of row.assignees) ids.add(a.entityId);
    }
    return Array.from(ids);
  }, [selectedRows]);

  const [value, setValue] = useState(initialIds);
  const prevRef = useRef(initialIds);

  const issueIds = useMemo(
    () => selectedRows.map((r) => r.issueId),
    [selectedRows],
  );

  const handleValueChange = useCallback(
    (nextIds: string[]) => {
      const prevSet = new Set(prevRef.current);
      const nextSet = new Set(nextIds);

      const added = nextIds.filter((id) => !prevSet.has(id));
      const removed = prevRef.current.filter((id) => !nextSet.has(id));

      prevRef.current = nextIds;
      setValue(nextIds);

      const mode = selectAllWhere ? BulkMode.Where : BulkMode.Ids;

      if (added.length > 0) {
        const p = selectAllWhere
          ? bulkMutations.addAssigneesByWhere(selectAllWhere, added)
          : bulkMutations.addAssignees(issueIds, added);
        p.then(() => {
          toastManager.add({ title: "Assignees updated", type: "success" });
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateAssignees,
            data: { view, scope, operation: BulkOperation.Add, mode, count: added.length },
          });
        }).catch((err) => toastMutationError(err, "Failed to update assignees"));
      }

      if (removed.length > 0) {
        const p = selectAllWhere
          ? bulkMutations.removeAssigneesByWhere(selectAllWhere, removed)
          : bulkMutations.removeAssignees(issueIds, removed);
        p.then(() => {
          toastManager.add({ title: "Assignees updated", type: "success" });
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.BulkUpdateAssignees,
            data: { view, scope, operation: BulkOperation.Remove, mode, count: removed.length },
          });
        }).catch((err) => toastMutationError(err, "Failed to update assignees"));
      }
    },
    [bulkMutations, issueIds, selectAllWhere, view, scope, track],
  );

  const handleSetPrimary = useCallback(
    (entityId: string) => {
      const p = selectAllWhere
        ? bulkMutations.setPrimaryAssigneeByWhere(selectAllWhere, entityId)
        : bulkMutations.setPrimaryAssignee(issueIds, entityId);
      p.then(() => {
        toastManager.add({ title: "Primary assignee updated", type: "success" });
        track({
          context: AnalyticsContext.Issue,
          event: IssueEvent.BulkUpdateAssignees,
          data: {
            view,
            scope,
            operation: BulkOperation.SetPrimary,
            mode: selectAllWhere ? BulkMode.Where : BulkMode.Ids,
          },
        });
      }).catch((err) => toastMutationError(err, "Failed to update assignees"));
    },
    [bulkMutations, issueIds, selectAllWhere, view, scope, track],
  );

  const selectedRefs = useMemo(() => {
    return selectedRows.flatMap((r) => r.assignees);
  }, [selectedRows]);

  return (
    <AssigneeSelector
      variant="inline"
      mode="list"
      allowSetPrimary
      showSetPrimaryOnAll
      showPrimarySubtext={false}
      value={value}
      onValueChange={handleValueChange}
      onSetPrimary={handleSetPrimary}
      selectedRefs={selectedRefs}
    />
  );
}
